// API Client - Base HTTP client for Blidz APIs
// This file provides the core HTTP client with proper typing and error handling
//
// ============================================================================
// Sentry Integration
// ============================================================================
// This client captures and sends the following data to Sentry for failed requests to
// our backend only. Successful requests send nothing here — Sentry's own
// breadcrumbsIntegration already records every fetch, enriched in app/root.tsx with the
// one field it cannot see itself (blidz_request_id, from a response header).
//
// REQUEST HEADERS:
// - x-csrftoken (presence and length only — never the raw token)
// - device-id
// - x-blidz-client-version
//
// REQUEST COOKIES (presence and length only — never the raw value):
// - csrftoken
// - blidz_session_id
//
// RESPONSE HEADERS:
// - http-x-blidz-request-id
//
// Note: Request bodies are captured only when errors occur (for debugging).
// Non-JSON error response bodies (e.g. a Django error page or nginx 502) are captured
// too, truncated to MAX_CAPTURED_BODY_LENGTH. Successful and JSON response bodies are
// not captured.
// ============================================================================

import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { devError, devLog } from "~/lib/dev-log";
import type { ApiResponse } from "./common";
import { CLIENT_CONFIG, SESSION_COOKIE_NAME } from "~/config/client";
import { getSessionGeneration, getSessionState, reestablishSession, whenSessionSettled } from "~/lib/session-gate";

// ============================================================================
// Configuration
// ============================================================================

/** Cap on how much of a non-JSON response body is attached to a Sentry event. */
export const MAX_CAPTURED_BODY_LENGTH = 2048;

/**
 * Retry budget for endpoints that are safe to repeat: pure reads, or writes proven
 * idempotent (e.g. an upsert keyed so a second call cannot create a duplicate).
 * The retry loop runs `attempt <= retries`, so this yields 3 requests in total.
 * Deliberately opt-in: `request()` defaults to 0 so that a write endpoint is never
 * retried by accident. Only add it to an endpoint where you have confirmed repeating
 * the call cannot produce a duplicate side effect.
 */
export const READ_RETRIES = 2;

/**
 * Per-attempt timeout for requests that opt into retries. Shorter than the default
 * so the retry budget does not multiply the worst-case wait: 3 attempts at 10s plus
 * backoff is ~33s in total, close to a single 30s attempt without retries.
 */
export const RETRYABLE_ATTEMPT_TIMEOUT = 10000;

/**
 * Retry budget for the auth endpoints that establish a session. Every other request in
 * the app depends on that session existing, so a single dropped `create_account` leaves
 * the whole page issuing calls that can only 403 — one failed bootstrap was observed
 * producing ~20 downstream errors. Same value as `READ_RETRIES`; kept separate so the
 * two policies can move independently.
 *
 * Pair it with `retryOnTimeout: false` on endpoints that create a record.
 */
export const AUTH_RETRIES = 2;

/**
 * Error codes that mean "this request needs a session and the one we used is gone".
 *
 * The narrowness is the safety argument, not a style choice: both codes are raised by the
 * backend's auth layer, which runs *before* the view, so the server provably did nothing.
 * That is what makes replaying safe for any method, POST included — the criterion is
 * always "can we prove the server did not act?", never the HTTP verb. It is the same test
 * that forbids retrying a timeout, applied in the direction where it passes.
 */
const SESSION_REPLAY_CODES = new Set(["session_id_missing", "session_not_found"]);

/** Digits, a 24-char Mongo ObjectId, or a UUID — path segments that carry an identifier. */
const ID_LIKE_SEGMENT = /^(?:\d+|[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * Reduce a request URL to a stable grouping key: drop the query string and replace
 * identifier segments, so /api/product/12345/live_deals does not fingerprint per product.
 */
function normalizeApiPath(url: string): string {
  try {
    return new URL(url).pathname
      .split("/")
      .map(segment => (ID_LIKE_SEGMENT.test(segment) ? ":id" : segment))
      .join("/");
  } catch {
    return "unknown";
  }
}

export interface ApiFailureReport {
  method: string;
  /** Absolute request URL. Reduced to a normalized path for grouping. */
  url: string;
  status: number;
  /** Low-cardinality error code. Used in the fingerprint alongside method/path/status. */
  code: string;
  requestHeaders?: Record<string, string>;
  requestCookies?: Record<string, string>;
  requestBody?: unknown;
  responseHeaders?: Record<string, string>;
  /** Truncated response body, for a non-JSON or non-conforming error payload. */
  responseBody?: string;
  /** Developer-facing diagnostic detail. High cardinality — context only. */
  detail?: string;
}

/**
 * Send an API failure to Sentry with an explicit fingerprint.
 *
 * Grouping keys on (method, normalized path, status, code) and never on the message,
 * because response bodies and diagnostic details are high-cardinality and wreck
 * default grouping.
 */
export function reportApiFailure(error: Error, report: ApiFailureReport): void {
  const normalizedPath = normalizeApiPath(report.url);
  const requestId = report.responseHeaders?.["http-x-blidz-request-id"];

  Sentry.withScope(scope => {
    // Group on what identifies the defect, never on the message — response bodies and
    // diagnostic details are high-cardinality and wreck default grouping.
    scope.setFingerprint([report.method, normalizedPath, String(report.status), report.code || "unknown"]);

    scope.setTag("api_error", true);
    scope.setTag("http_status", String(report.status));
    scope.setTag("api_endpoint", normalizedPath);
    if (requestId) {
      scope.setTag("blidz_request_id", requestId);
    }

    scope.setContext("api_request", {
      headers: report.requestHeaders,
      cookies: report.requestCookies,
      body: report.requestBody,
    });

    scope.setContext("api_response", {
      headers: report.responseHeaders,
      body: report.responseBody,
      detail: report.detail,
    });

    Sentry.captureException(error);
  });
}

export interface ApiClientConfig {
  baseUrl: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
  retryDelay?: number;
}

// ============================================================================
// Request Types
// ============================================================================

export interface RequestConfig {
  method?: "GET" | "POST";
  headers?: Record<string, string>;
  params?: Record<string, any>;
  timeout?: number;
  retries?: number;
  // Whether a timed-out attempt may be retried. Defaults to true, which is correct for
  // reads and for endpoints that only look something up. Set it to false on an endpoint
  // that creates a record: a timeout means the response never arrived, NOT that the
  // server never acted, so repeating the call can produce a duplicate. A network error
  // is still retried in that case — the connection never carried the request through.
  retryOnTimeout?: boolean;
  // Flag to indicate if this is a signup/login request (excludes CSRF token)
  isAuthRequest?: boolean;
  /**
   * Whether this endpoint needs a session.
   *
   * `"required"` (the default, fail-closed) waits for the session to settle, and fails
   * locally without sending anything if it settled as unavailable — a request that can
   * only come back 401 is not worth a round trip, and sending it anyway is what turned one
   * failed bootstrap into twenty Sentry events.
   *
   * `"none"` sends immediately. Only for endpoints that are CSRF-exempt on the backend and
   * return useful data to an anonymous caller (today: `/deal/deal_data`). The csrftoken is
   * still attached when a cookie happens to exist.
   */
  session?: "required" | "none";
}

// ============================================================================
// Sentry Integration Types
// ============================================================================

/**
 * The result of reading a response, separated from the decision of what to do about it.
 * Keeping interpretation free of side effects is what makes a replayed 401 reportable-once.
 */
type ResponseOutcome<T> = { ok: true; data: ApiResponse<T> } | { ok: false; error: ApiClientError };

interface SentryApiContext {
  requestHeaders: Record<string, string>;
  requestCookies: Record<string, string>;
  responseHeaders: Record<string, string>;
  url: string;
  method: string;
  requestBody?: any;
  statusCode?: number;
  /** Truncated non-JSON response body (Django error page, nginx 502) for debugging. */
  responseBody?: string;
}

// ============================================================================
// API Client Class
// ============================================================================

export class ApiClient {
  private config: Required<ApiClientConfig>;
  private deviceId?: string;
  private clientVersion?: string;

  constructor(config: ApiClientConfig) {
    this.config = {
      timeout: 30000,
      defaultHeaders: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      retryDelay: 1000,
      ...config,
    };
  }

  // ============================================================================
  // Device Management
  // ============================================================================

  setDeviceInfo(deviceId: string, clientVersion: string) {
    this.deviceId = deviceId;
    this.clientVersion = clientVersion;
  }

  // ============================================================================
  // Cookie Utilities
  // ============================================================================

  /**
   * Attach the csrftoken read from the cookie jar *right now*.
   *
   * Must be called as late as possible — immediately before `fetch`, inside the retry
   * loop. `document.cookie` is mutated by the browser's network stack, not by JS tasks, so
   * any in-flight response carrying `Set-Cookie` can change it between two statements
   * separated by nothing more than an `await`. Building this header once outside the retry
   * loop meant a request that started before the session existed still sent no token on a
   * retry ten seconds later, long after the cookie had arrived.
   *
   * Auth requests send no token: they are CSRF-exempt on the backend and are what creates
   * the token in the first place.
   */
  private withFreshCsrfToken(headers: Record<string, string>, isAuthRequest: boolean): Record<string, string> {
    if (isAuthRequest) return headers;

    const csrfToken = Cookies.get("csrftoken");
    if (!csrfToken) {
      devLog("ℹ️ No CSRF token in headers - relying on automatic cookie transmission");
      return headers;
    }
    return { ...headers, "X-CSRFToken": csrfToken };
  }

  // ============================================================================
  // Sentry Integration
  // ============================================================================

  /**
   * Format a credential value as presence-and-length rather than the raw value, so a
   * Sentry payload can carry the diagnostic signal (missing token, truncated token)
   * without ever shipping something that can be replayed.
   */
  private static formatCredentialPresence(value: string | undefined): string {
    return value ? `present(${value.length})` : "absent";
  }

  /**
   * Capture specific request headers for Sentry
   */
  private captureRequestHeaders(requestHeaders: Record<string, string>): Record<string, string> {
    const capturedHeaders: Record<string, string> = {};

    // Device-Id and x-blidz-client-version are not credentials, so the real value is
    // useful and captured as-is. X-CSRFToken is a credential: never emit its value, only
    // presence and length (see captureRequestCookies for the rationale). Authorization is
    // intentionally not in this list: userApi.tokenLogin passes it as an additional header
    // (`Authorization: Token <token>`), which buildHeaders merges into the final headers —
    // so before the previous commit, a failed token_login really did send the raw bearer
    // token to Sentry. Leaving it out of capture here closes that leak.
    capturedHeaders["X-CSRFToken"] = ApiClient.formatCredentialPresence(requestHeaders["X-CSRFToken"]);

    const passthroughHeaders = ["Device-Id", "x-blidz-client-version"];
    passthroughHeaders.forEach(headerName => {
      const value = requestHeaders[headerName];
      if (value) {
        capturedHeaders[headerName] = value;
      }
    });

    return capturedHeaders;
  }

  /**
   * Capture specific request cookies for Sentry
   *
   * Never emit cookie values — these are live credentials and Sentry is not a place to
   * keep them. Presence and length preserve the diagnostic signal (missing token,
   * truncated token) without shipping anything that can be replayed.
   */
  private captureRequestCookies(): Record<string, string> {
    const capturedCookies: Record<string, string> = {};

    // Capture specific cookies we want to track. Always include both keys — unlike the
    // headers above, an absent cookie is reported as "absent" rather than omitted,
    // because "absent" and "not captured" are different facts when debugging auth.
    const cookiesToCapture = ["csrftoken", SESSION_COOKIE_NAME];

    cookiesToCapture.forEach(cookieName => {
      capturedCookies[cookieName] = ApiClient.formatCredentialPresence(Cookies.get(cookieName));
    });

    return capturedCookies;
  }

  /**
   * Capture specific response headers for Sentry
   */
  private captureResponseHeaders(response: Response): Record<string, string> {
    const capturedHeaders: Record<string, string> = {};

    // Capture specific headers we want to track. content-length and content-type are
    // what turn a parse failure into a diagnosis: comparing the advertised length to the
    // bytes actually received tells a truncated response apart from a malformed one.
    const headersToCapture = ["http-x-blidz-request-id", "content-length", "content-type"];

    headersToCapture.forEach(headerName => {
      const value = response.headers.get(headerName);
      if (value) {
        capturedHeaders[headerName] = value;
      }
    });

    return capturedHeaders;
  }

  /**
   * Check if request is to our backend (to avoid capturing headers for external requests)
   */
  private isBackendRequest(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;

      // Check if it's same origin (development with proxy)
      if (urlObj.origin === currentOrigin) {
        return true;
      }

      // Check if it's our backend domain (production/staging). Compare against *this
      // instance's* configured base URL — mirroring `buildUrl`, which is what actually
      // produced `url` — rather than the env var directly. Reading the env var here only
      // agreed with `buildUrl` by coincidence: there is exactly one `ApiClient` today and
      // it happens to be constructed from that same env var. A second instance pointed at
      // a different origin would otherwise never match here, and its failures — already
      // suppressed at call sites as `ApiClientError` — would go completely unreported.
      const baseUrl = this.config.baseUrl || window.location.origin;
      const apiUrlObj = new URL(baseUrl);
      return urlObj.origin === apiUrlObj.origin;
    } catch {
      return false;
    }
  }

  /**
   * Read a response body as text, reporting a stream-level read failure rather than
   * letting it escape as a bare TypeError.
   *
   * Always read a body this way instead of calling `response.json()`. `json()` consumes
   * the stream, so once it throws the body is unrecoverable — and a parse failure is
   * precisely the case where the body is the whole diagnosis. Parse the returned string
   * yourself, and attach it to the Sentry context when the parse fails.
   */
  private async readBodyText(response: Response, context: SentryApiContext): Promise<string> {
    try {
      return await response.text();
    } catch (readError) {
      // The stream broke mid-read (connection dropped, decoding error). There is no body
      // to attach, but the distinct code separates it from a malformed payload.
      const error = new ApiClientError(response.status, "BODY_READ_ERROR", (readError as Error).message);
      this.sendToSentry(context, error);
      throw error;
    }
  }

  /** Send API context to Sentry. */
  private sendToSentry(context: SentryApiContext, error: Error) {
    // Only capture for backend requests
    if (!this.isBackendRequest(context.url)) {
      return;
    }

    reportApiFailure(error, {
      method: context.method,
      url: context.url,
      status: context.statusCode ?? 0,
      // For a non-ApiClientError (e.g. TypeError: Failed to fetch) fall back to the error
      // name, so network failures group by endpoint rather than by browser wording
      // ("Failed to fetch" in Chrome vs "Load failed" in Safari).
      code: error instanceof ApiClientError ? error.code : error.name,
      requestHeaders: context.requestHeaders,
      requestCookies: context.requestCookies,
      requestBody: context.requestBody,
      responseHeaders: context.responseHeaders,
      responseBody: context.responseBody,
      detail: error instanceof ApiClientError ? error.detail : undefined,
    });
  }

  // ============================================================================
  // Core Request Method
  // ============================================================================

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const {
      method = "GET",
      headers = {},
      params = {},
      timeout,
      // Fail-closed: no endpoint is retried unless it opted in with READ_RETRIES.
      // Method is not a useful signal here — every endpoint in this app is a POST,
      // including the pure reads. Clamped to 0: a negative value would skip the loop
      // body entirely and leave `lastError` unassigned below.
      retries: rawRetries = 0,
      retryOnTimeout = true,
      isAuthRequest = false,
      // Auth requests are what establishes the session, so they must never wait for one —
      // the gate's own flow is made of these calls, and waiting would deadlock it.
      session = isAuthRequest ? "none" : "required",
    } = config;
    const retries = Math.max(0, rawRetries);

    // A retrying request gets a shorter per-attempt budget so the retry count does not
    // multiply the worst-case wait. An explicit `timeout` from the caller always wins.
    //
    // This only applies when timeouts are retryable. With `retryOnTimeout: false` at most
    // one attempt can ever time out, so the budget cannot multiply — shortening it there
    // would buy nothing and would fail slow-but-healthy requests that used to succeed.
    const shortenTimeout = retries > 0 && retryOnTimeout;
    const effectiveTimeout = timeout ?? (shortenTimeout ? RETRYABLE_ATTEMPT_TIMEOUT : this.config.timeout);

    const url = this.buildUrl(endpoint);

    // Handle params based on HTTP method
    let requestBody: any = undefined;

    if (method === "GET") {
      // For GET requests, add params to URL query parameters
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    } else if (method === "POST") {
      // For POST requests, use params as the request body
      if (Object.keys(params).length > 0) {
        requestBody = params;
      }
    }

    const baseHeaders = this.buildHeaders(headers);
    const body = requestBody && method === "POST" ? JSON.stringify(requestBody) : undefined;

    // Two nested loops on deliberately different axes:
    //   outer — the session. One replay at most: if the session we used turned out to be
    //           gone, re-establish it and do the whole request again with fresh headers.
    //   inner — the transport. `retries` attempts for network errors and (unless the
    //           endpoint opted out) timeouts, reusing everything but the abort signal.
    // The session loop has to be the outer one because a replay needs a NEW csrftoken, and
    // the transport loop deliberately reuses its request options.
    for (let sessionAttempt = 0; sessionAttempt <= 1; sessionAttempt++) {
      if (session === "required") {
        await whenSessionSettled();
        if (getSessionState() === "unavailable") {
          // Nothing is sent. A request that can only come back 401 is not worth a round
          // trip, and sending it anyway is what turned one failed bootstrap into twenty
          // Sentry events. The flow already reported why the session is unavailable, so
          // this failure is a consequence and is deliberately not reported again.
          throw new ApiClientError(
            0,
            "SESSION_UNAVAILABLE",
            "No session could be established for this page load",
            "Something went wrong. Please reload the page."
          );
        }
      }

      const requestOptions: RequestInit = {
        method,
        credentials: "include", // Include cookies in cross-origin requests
      };
      if (body) {
        requestOptions.body = body;
      }

      // Which session this attempt runs against. Compared again if it 401s.
      const sessionGeneration = getSessionGeneration();
      let response: Response | null = null;
      let transportError: ApiClientError | null = null;
      let sentryContext: SentryApiContext | null = null;

      for (let attempt = 0; attempt <= retries; attempt++) {
        // Read the csrftoken here, as close to the wire as we can get. See
        // `withFreshCsrfToken` for why doing it once outside this loop is a bug.
        const attemptHeaders = this.withFreshCsrfToken(baseHeaders, isAuthRequest);
        sentryContext = {
          requestHeaders: this.captureRequestHeaders(attemptHeaders),
          requestCookies: this.captureRequestCookies(),
          responseHeaders: {},
          url: url.toString(),
          method,
          // Auth request bodies carry live credentials (idToken, token, code) that would be
          // replayable by anyone with Sentry access. Other endpoints' bodies are useful for
          // debugging; these are omitted outright rather than redacted field-by-field, so a
          // newly added credential field cannot leak through a stale denylist.
          requestBody: isAuthRequest ? undefined : requestBody,
        };

        // Only the network call is inside this try. A failure here is always transport
        // level — fetch rejects with TypeError ("Failed to fetch" / "Load failed") or the
        // timeout signal's TimeoutError — so every caught error is safe to retry. Response
        // handling is deliberately outside, so a bug thrown while parsing is never retried
        // and never misreported as a network failure.
        try {
          response = await fetch(url.toString(), {
            ...requestOptions,
            headers: attemptHeaders,
            // A fired AbortSignal stays aborted, so each attempt needs a fresh one.
            signal: AbortSignal.timeout(effectiveTimeout),
          });
          transportError = null;
          break;
        } catch (error) {
          // This catch wraps only `fetch`, so the failure is always transport level.
          // Normalising it here means exactly one error type crosses the API boundary,
          // and `instanceof ApiClientError` becomes a complete test for "an API call failed".
          const cause = error as Error;
          const isTimeout = cause.name === "TimeoutError" || cause.name === "AbortError";
          transportError = new ApiClientError(
            0,
            isTimeout ? "TIMEOUT_ERROR" : "NETWORK_ERROR",
            // Browser wording differs ("Failed to fetch" / "Load failed" /
            // "NetworkError when attempting to fetch resource."). Keep it as diagnostic
            // detail so it reaches Sentry context but never the title or the fingerprint.
            cause.message,
            isTimeout ? "Request timed out. Please try again." : "Network error. Please try again."
          );
          sentryContext.statusCode = 0;
          response = null;

          // A timeout is the one transport failure that does not prove the server stayed
          // untouched: the request may have been processed and only the response lost. On
          // an endpoint that creates a record, retrying that is how duplicates are born.
          if (attempt === retries || (isTimeout && !retryOnTimeout)) {
            break;
          }

          await this.delay(this.config.retryDelay * Math.pow(2, attempt));
        }
      }

      // `sentryContext` is assigned on the first pass of a loop that always runs at least
      // once (`retries` is clamped to >= 0), so this is a type narrowing, not a fallback.
      const context = sentryContext!;

      if (!response) {
        // Transport failed every attempt. Not replayable: a new session would not help.
        this.sendToSentry(context, transportError!);
        throw transportError!;
      }

      context.responseHeaders = this.captureResponseHeaders(response);
      context.statusCode = response.status;

      const outcome = await this.interpretResponse<T>(response, context);
      if (outcome.ok) {
        return outcome.data;
      }

      if (sessionAttempt === 0 && !isAuthRequest && SESSION_REPLAY_CODES.has(outcome.error.code)) {
        Sentry.addBreadcrumb({
          category: "session",
          level: "info",
          message: "replaying request after a session 401",
          data: { url: url.toString(), code: outcome.error.code },
        });
        // If the session already moved on while this request was in flight, another caller
        // has re-authenticated and this one only needs replaying. Skipping the re-auth here
        // is what makes "one re-auth per outage" structural instead of merely likely: a
        // straggler whose 401 lands after recovery would otherwise start a second flow.
        if (getSessionGeneration() !== sessionGeneration) {
          continue;
        }
        await reestablishSession();
        if (getSessionState() === "ready") {
          // Deliberately not reported: this 401 was transient and we recovered from it.
          continue;
        }
      }

      // The single terminal report for this request, replacing the three scattered
      // `sendToSentry` calls that used to live inside response handling.
      this.sendToSentry(context, outcome.error);
      throw outcome.error;
    }

    // Unreachable: every path inside the loop returns or throws.
    throw new ApiClientError(0, "SESSION_REPLAY_EXHAUSTED", "session replay loop fell through");
  }

  // ============================================================================
  // Specialized Request Methods
  // ============================================================================

  /**
   * Handler for authentication-related API calls (signup/login).
   *
   * These are CSRF-exempt on the backend and send no csrftoken, and they never wait for a
   * session because they are what produces one.
   *
   * This method deliberately does NOT touch the session gate. Sequencing belongs to the
   * flow in `api/session.ts`, which knows whether a failure here is the end of the attempt
   * or the cue for a fallback. Opening the gate from a single call's `finally` — as this
   * used to — released every queued request in the window between `token_login` failing and
   * `create_account` starting.
   *
   * Pass `retryOnTimeout: false` for any endpoint that creates a record; see
   * `RequestConfig.retryOnTimeout`.
   */
  async makeAuthRequest<TResponse = any>(
    endpoint: string,
    params?: any,
    additionalHeaders?: Record<string, string>,
    retries = 0,
    retryOnTimeout = true
  ): Promise<TResponse> {
    try {
      const config: RequestConfig = {
        method: "POST",
        headers: additionalHeaders || {},
        params: params || {},
        isAuthRequest: true,
        retries,
        retryOnTimeout,
      };

      const response = await this.request<any>(endpoint, config);

      devLog(`✅ AUTH - Response received:`, response);

      // The backend returns a Blidz API response
      // Extract the content or return the response as-is
      const responseData = response.content || response;

      return responseData as TResponse;
    } catch (error) {
      devError(`❌ AUTH - Error:`, error);
      throw error;
    }
  }

  /**
   * Handler for regular API calls that require authentication
   * These requests will include CSRF tokens and other session headers
   */
  async makeAuthenticatedRequest<TResponse = any>(
    endpoint: string,
    options: {
      method?: "GET" | "POST";
      params?: Record<string, any>;
      headers?: Record<string, string>;
      session?: "required" | "none";
      retries?: number;
      retryOnTimeout?: boolean;
    } = {}
  ): Promise<TResponse> {
    const { method = "POST", params = {}, headers = {}, session, retries, retryOnTimeout } = options;

    try {
      const config: RequestConfig = {
        method,
        headers,
        params,
        isAuthRequest: false,
        session,
        retries,
        retryOnTimeout,
      };

      const response = await this.request<any>(endpoint, config);

      devLog(`✅ AUTHENTICATED - Response received:`, response);

      // The backend returns a Blidz API response
      // Extract the content or return the response as-is
      const responseData = response.content || response;

      return responseData as TResponse;
    } catch (error) {
      devError(`❌ AUTHENTICATED - Error:`, error);
      throw error;
    }
  }

  /**
   * Handler for file upload requests using modern Fetch API
   * These requests use FormData and require authentication
   */
  async makeFileUploadRequest<TResponse = any>(
    endpoint: string,
    files: File[],
    additionalData?: Record<string, string>,
    options?: {
      headers?: Record<string, string>;
      timeout?: number;
      onProgress?: (progress: number) => void;
    }
  ): Promise<TResponse> {
    const { headers = {}, timeout = this.config.timeout } = options || {};

    try {
      const url = this.buildUrl(endpoint);

      // Create FormData
      const formData = new FormData();

      // Add files to FormData
      files.forEach(file => {
        formData.append("files", file);
      });

      // Add additional data if provided
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      // A record-creating POST, so it waits for a session like every other request. It
      // used to get this for free from `await buildHeaders`, which awaited the CSRF gate;
      // making header construction synchronous silently dropped it, leaving an upload
      // submitted during the bootstrap to go out with neither csrftoken nor session.
      await whenSessionSettled();
      if (getSessionState() === "unavailable") {
        throw new ApiClientError(
          0,
          "SESSION_UNAVAILABLE",
          "No session could be established for this page load",
          "Something went wrong. Please reload the page."
        );
      }

      // Build headers (exclude Content-Type to let browser set it with boundary)
      const requestHeaders = this.withFreshCsrfToken(this.buildHeaders(headers), false);
      delete requestHeaders["Content-Type"]; // Let browser set multipart/form-data boundary

      // Prepare Sentry context
      const sentryContext: SentryApiContext = {
        requestHeaders: this.captureRequestHeaders(requestHeaders),
        requestCookies: this.captureRequestCookies(),
        responseHeaders: {},
        url: url.toString(),
        method: "POST",
        requestBody: { files: files.map(f => f.name), ...additionalData },
      };

      // Create AbortController for timeout handling
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, timeout);

      // Only the network call is inside this try, mirroring `request()`: anything caught
      // here is transport level by construction — fetch rejects with an AbortError (the
      // timeout above firing) or a TypeError for a failed transport. Browser wording
      // differs ("Failed to fetch" in Chrome, "Load failed" in Safari); matching on that
      // text missed the Safari case entirely, so a raw TypeError used to escape past this
      // point. Keying on `error.name` instead — same as `request()` — catches both, and
      // keeping response handling outside this try means an `ApiClientError` thrown below
      // is never re-caught here and re-reported, and a bug in our own code is never
      // misreported as a network failure.
      let response: Response;
      try {
        response = await fetch(url.toString(), {
          method: "POST",
          headers: requestHeaders,
          body: formData,
          signal: abortController.signal,
        });
      } catch (error) {
        clearTimeout(timeoutId);
        const cause = error as Error;
        const isTimeout = cause.name === "AbortError";
        const uploadError = new ApiClientError(
          isTimeout ? 408 : 0,
          isTimeout ? "TIMEOUT_ERROR" : "NETWORK_ERROR",
          isTimeout ? "Upload timeout" : cause.message,
          isTimeout ? "Request timed out. Please try again." : "Network error. Please try again."
        );
        this.sendToSentry(sentryContext, uploadError);
        throw uploadError;
      }

      clearTimeout(timeoutId);

      // Capture response headers for Sentry
      sentryContext.responseHeaders = this.captureResponseHeaders(response);
      sentryContext.statusCode = response.status;

      if (!response.ok) {
        const rawBody = await this.readBodyText(response, sentryContext);
        let errorData: any = {};
        try {
          errorData = JSON.parse(rawBody);
        } catch {
          sentryContext.responseBody = rawBody.slice(0, MAX_CAPTURED_BODY_LENGTH);
        }

        const error_code = errorData.code || "upload_error";
        const error_user_message = errorData.userMessage || "Failed to upload files. Please try again.";
        const clientError = new ApiClientError(
          response.status,
          error_code,
          errorData.reason ?? errorData.message,
          error_user_message
        );
        this.sendToSentry(sentryContext, clientError);
        throw clientError;
      }

      // Parse successful response. Same shape as the error branch above and as
      // handleResponse: read the body once as text, then parse, so a parse failure can
      // still report what it received instead of losing it with the consumed stream.
      const rawBody = await this.readBodyText(response, sentryContext);
      let responseData: any;
      try {
        responseData = JSON.parse(rawBody);
      } catch (parseError) {
        sentryContext.responseBody = rawBody.slice(0, MAX_CAPTURED_BODY_LENGTH);
        const error = new ApiClientError(
          response.status,
          "PARSE_ERROR",
          `${(parseError as Error).message} (received ${rawBody.length} chars)`
        );
        this.sendToSentry(sentryContext, error);
        throw error;
      }

      devLog(`✅ FILE UPLOAD - Response received:`, responseData);

      // The backend returns a Blidz API response
      // Extract the content or return the response as-is
      const responseContent = responseData.content || responseData;
      return responseContent as TResponse;
    } catch (error) {
      // Every throw above (the transport catch, the non-2xx branch, BODY_READ_ERROR and
      // PARSE_ERROR) is already an `ApiClientError` that has already called
      // `sendToSentry` at the throw site. This outer catch exists only to log and
      // rethrow — it must NOT call `sendToSentry` again, or every file-upload failure
      // reports twice.
      devError(`❌ FILE UPLOAD - Error:`, error);
      throw error;
    }
  }

  // ============================================================================
  // Header Building Logic
  // ============================================================================

  /**
   * Build everything about the headers that does not depend on the cookie jar.
   *
   * The csrftoken is deliberately NOT added here — see `withFreshCsrfToken`.
   */
  private buildHeaders(requestHeaders: Record<string, string> = {}): Record<string, string> {
    const headers = {
      ...this.config.defaultHeaders,
    };

    // Always add x-blidz-client-version for every request
    if (this.clientVersion) {
      headers["x-blidz-client-version"] = this.clientVersion;
    }

    if (this.deviceId) {
      headers["Device-Id"] = this.deviceId;
    }

    // Merge with per-request headers (per-request headers take precedence)
    return {
      ...headers,
      ...requestHeaders,
    };
  }

  // ============================================================================
  // Response Handling
  // ============================================================================

  /**
   * Interpret a response into success or a typed failure. Deliberately reports NOTHING.
   *
   * Reporting used to happen here, at three separate throw sites. It had to move out: a
   * 401 that we are about to recover from by re-establishing the session and replaying
   * must not be reported, and only the caller knows whether a replay is still available.
   * `request()` now reports exactly once, at whichever point it gives up.
   */
  private async interpretResponse<T>(response: Response, context: SentryApiContext): Promise<ResponseOutcome<T>> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    context.statusCode = response.status;

    let rawBody: string;
    try {
      rawBody = await response.text();
    } catch (readError) {
      // The stream broke mid-read (connection dropped, decoding error). There is no body
      // to attach, but the distinct code separates it from a malformed payload.
      return {
        ok: false,
        error: new ApiClientError(response.status, "BODY_READ_ERROR", (readError as Error).message),
      };
    }

    let responseData: any;
    if (isJson) {
      try {
        responseData = JSON.parse(rawBody);
      } catch (parseError) {
        context.responseBody = rawBody.slice(0, MAX_CAPTURED_BODY_LENGTH);
        // The parser's own message ("Unexpected end of JSON input", "unterminated string
        // at line 1 column 95") distinguishes truncation from corruption, and the received
        // length checked against the content-length header settles it. Both are
        // high-cardinality, so they go to the context — the fingerprint keys on
        // (method, path, status, PARSE_ERROR) and the title stays `HTTP <status> - <code>`.
        return {
          ok: false,
          error: new ApiClientError(
            response.status,
            "PARSE_ERROR",
            `${(parseError as Error).message} (received ${rawBody.length} chars)`
          ),
        };
      }
    } else {
      responseData = rawBody;
    }

    if (!response.ok) {
      const error_code = (isJson ? responseData.code : "api_error") || "api_error";
      // A non-JSON body is a document (Django error page, nginx 502), not a diagnostic
      // message. Putting it on the error is what split one defect into ~20 Sentry issues
      // and let raw markup reach the UI. It goes on the Sentry context instead.
      const error_detail = isJson ? (responseData.reason ?? responseData.message) : undefined;
      const error_user_message = isJson ? responseData.userMessage : "Failed. Please try again later";
      if (!isJson && typeof responseData === "string" && responseData.length > 0) {
        context.responseBody = responseData.slice(0, MAX_CAPTURED_BODY_LENGTH);
      }
      return {
        ok: false,
        error: new ApiClientError(response.status, error_code, error_detail, error_user_message),
      };
    }

    return { ok: true, data: responseData as ApiResponse<T> };
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ============================================================================
  // URL Building Helpers
  // ============================================================================

  buildUrl(endpoint: string): URL {
    // Ensure baseUrl ends with a slash
    let baseUrl = this.config.baseUrl;
    if (!baseUrl) {
      baseUrl = window.location.origin;
    }

    baseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

    // Ensure endpoint starts without a slash and prepend 'api/'
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    const apiEndpoint = `api/${cleanEndpoint}`;

    const url = new URL(apiEndpoint, baseUrl);
    devLog("🌐 URL:", url.toString());
    return url;
  }
}

// ============================================================================
// Custom Error Class
// ============================================================================

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Developer-facing detail supplementing `code`, e.g. "user_id: 8823741".
   * High-cardinality by design: never put this in the Sentry title, the
   * fingerprint, or the UI. Use `userMessage` for anything a user sees.
   */
  readonly detail?: string;
  /** Copy supplied by the backend for display to users. */
  readonly userMessage?: string;

  constructor(status: number, code: string, detail?: string, userMessage?: string) {
    super(`HTTP ${status} - ${code || "unknown"}`);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.userMessage = userMessage;
  }

  /**
   * True for a transport-level failure: `request()` wraps a failed `fetch` (a raw
   * `TypeError`/`DOMException` such as "Failed to fetch", "Load failed", or a timeout)
   * into an `ApiClientError` with `status === 0` and `code` `NETWORK_ERROR` or
   * `TIMEOUT_ERROR` before it crosses the API boundary. `makeFileUploadRequest` also
   * constructs status 0 / NETWORK_ERROR / TIMEOUT_ERROR errors directly for the same
   * cases on the upload path.
   */
  get isNetworkError(): boolean {
    if (
      this.status === 0 ||
      this.status === 408 ||
      this.status === 504 ||
      this.code === "NETWORK_ERROR" ||
      this.code === "TIMEOUT_ERROR"
    ) {
      return true;
    }

    return false;
  }
}

/**
 * Text safe to display to a user for a failed API call.
 * Never throws and never returns undefined — falls back to the caller's own copy when
 * the backend supplied no `userMessage`, or when the failure was not an API failure.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiClientError && error.userMessage ? error.userMessage : fallback;
}

/**
 * Report an error that the API client did not already report.
 *
 * Every `ApiClientError` has already been sent to Sentry by `sendToSentry` with the
 * request context, an explicit fingerprint and the backend request id — reporting it
 * again from a call site produces a duplicate with none of that context. Anything else
 * reaching a call site's catch is a bug in our own code, which nothing else would report.
 */
export function reportUnexpectedError(error: unknown): void {
  if (error instanceof ApiClientError) {
    return;
  }
  Sentry.captureException(error);
}

// ============================================================================
// Default Client Instance
// ============================================================================

// Generate a unique device ID for this browser session
export const generateDeviceId = (): string => {
  // Check if we're in a browser environment
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    // Server-side or environment without localStorage
    const randomPart = Math.random().toString(36).substr(2, 9);
    const timePart = Date.now().toString(36);
    return `web-ssr-${randomPart}-${timePart}`;
  }

  // Try to get from localStorage first
  let deviceId = localStorage.getItem("blidz-device-id");
  if (!deviceId) {
    // Generate a new UUID-like device ID
    const randomPart = Math.random().toString(36).substr(2, 9);
    const timePart = Date.now().toString(36);
    deviceId = `web-${randomPart}-${timePart}`;
    localStorage.setItem("blidz-device-id", deviceId);
  }
  return deviceId;
};

// Configuration for different environments
// Note: Using Vite proxy for development to avoid CORS issues
// Set VITE_API_BASE_URL environment variable to override

// Get the API base URL
const getApiBaseUrl = (): string => {
  // Check environment variable first
  if (import.meta.env.VITE_API_BASE_URL) {
    devLog("Using API URL from environment:", import.meta.env.VITE_API_BASE_URL);
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Use relative URL for development (works with Vite proxy)
  // This avoids CORS issues by using the same origin
  const defaultUrl = "";
  devLog("Using relative API URL (via Vite proxy)");
  return defaultUrl;
};

// Create default client instance (can be configured later)
export const apiClient = new ApiClient({
  baseUrl: getApiBaseUrl(),
});

// Set required headers for Blidz backend
apiClient.setDeviceInfo(
  generateDeviceId(),
  CLIENT_CONFIG.VERSION // Client version
);
